// CommunicationList.js - Lista över utskick (Spår L). Används i patientvyns
// flik "Utskick" och i bokningsrutans sektion "Skickat". Klick på en rad visar texten.

import React, { useState } from 'react';
import { CHANNEL_LABELS, CATEGORY_LABELS, DELIVERY_LABELS } from './communicationsApi';
import './CommunicationList.css';

// 'unknown' = osäkert SMS-utfall: 46elks kan ha tagit emot SMS:et (Spår B5)
const STATUS_LABELS = { sent: 'Skickat', failed: 'Misslyckades', unknown: 'Osäkert utfall' };

const formatDateTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('sv-SE', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

// E-posttexter sparas som HTML. Visa dem som ren text (ingen HTML renderas).
// DOMParser kör inga skript och laddar inga resurser.
const toPlainText = (body) => {
  if (!body) return '';
  if (!/<[a-z][\s\S]*>/i.test(body)) return body;
  const withBreaks = body
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n');
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
  return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
};

const CommunicationList = ({ items, compact = false, emptyText = 'Inga utskick' }) => {
  const [openId, setOpenId] = useState(null);

  if (!items || items.length === 0) {
    return <div className="comm-empty">{emptyText}</div>;
  }

  return (
    <ul className={`comm-list ${compact ? 'comm-list-compact' : ''}`}>
      {items.map(item => {
        const channel = CHANNEL_LABELS[item.channel] || { label: item.channel, icon: 'fa-paper-plane' };
        const isOpen = openId === item.comm_id;
        const text = isOpen ? toPlainText(item.body) : '';
        return (
          <li key={item.comm_id} className={`comm-item ${isOpen ? 'open' : ''}`}>
            <button
              type="button"
              className="comm-row"
              onClick={() => setOpenId(isOpen ? null : item.comm_id)}
              aria-expanded={isOpen}
            >
              <span className="comm-date">{formatDateTime(item.sent_at)}</span>
              <span className="comm-channel" title={channel.label}>
                <i className={`fas ${channel.icon}`}></i> {channel.label}
              </span>
              <span className="comm-category">
                {CATEGORY_LABELS[item.category] || item.category}
                {item.trigger === 'automatic' && <span className="comm-auto">auto</span>}
              </span>
              {!compact && <span className="comm-recipient">{item.recipient || '–'}</span>}
              <span className={`comm-status comm-status-${item.status}`}>
                {STATUS_LABELS[item.status] || 'Misslyckades'}
              </span>
              <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} comm-toggle`}></i>
            </button>

            {isOpen && (
              <div className="comm-detail">
                <dl className="comm-meta">
                  {compact && item.recipient && (<><dt>Mottagare</dt><dd>{item.recipient}</dd></>)}
                  {item.subject && (<><dt>Ämne</dt><dd>{item.subject}</dd></>)}
                  {item.delivery && (<><dt>Leverans</dt><dd>{DELIVERY_LABELS[item.delivery] || item.delivery}</dd></>)}
                  {item.appointment_start && !compact && (
                    <><dt>Bokning</dt><dd>{formatDateTime(item.appointment_start)}{!item.appointment_id && ' (borttagen)'}</dd></>
                  )}
                  <dt>Skickat av</dt><dd>{item.sent_by_name || 'Systemet'}</dd>
                  {item.status !== 'sent' && item.error_message && (
                    <><dt>Fel</dt><dd className="comm-error">{item.error_message}</dd></>
                  )}
                </dl>
                {text
                  ? <div className="comm-body">{text}</div>
                  : <div className="comm-body comm-body-missing">Texten sparades inte för detta utskick.</div>}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default CommunicationList;
